import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { resetPassword } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border-b border-[#FDFBF7]/20 focus:border-[#D4AF37] outline-none py-3 font-body text-[#FDFBF7] placeholder:text-[#A89F91]/60 transition-colors duration-300";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters.");
    if (pw !== confirm) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      await resetPassword(token, pw);
      setDone(true);
      toast.success("Password updated. You can now sign in.");
      setTimeout(() => navigate("/admin/login"), 1800);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-pof flex items-center justify-center px-6">
      <div className="absolute top-8 left-8">
        <Link to="/admin/login" className="inline-flex items-center gap-2 font-body text-xs tracking-[0.2em] uppercase text-[#A89F91] hover:text-[#D4AF37] transition-colors">
          <ArrowLeft size={15} /> Back to Sign In
        </Link>
      </div>
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="w-full max-w-md">
        <div className="text-center mb-10">
          <img src="/logo.png" alt="Palace of Flavor" className="h-24 w-auto mx-auto mb-6" />
          <p className="font-body text-xs tracking-[0.4em] uppercase text-[#D4AF37]">Admin Portal</p>
          <h1 className="font-display text-4xl text-[#FDFBF7] mt-2">Set New Password</h1>
        </div>

        {!token ? (
          <p data-testid="reset-invalid" className="text-center font-body text-[#C92A6B]">This reset link is missing its token. Please use the link from your email.</p>
        ) : done ? (
          <div className="text-center space-y-4">
            <CheckCircle2 size={48} className="mx-auto text-[#0F7A54]" />
            <p data-testid="reset-done" className="font-body text-[#A89F91]">Your password has been updated. Redirecting to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-form" className="space-y-8">
            <input data-testid="reset-new" type="password" className={inputCls} placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} />
            <input data-testid="reset-confirm" type="password" className={inputCls} placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            <button data-testid="reset-submit" type="submit" disabled={loading} className="w-full flex items-center justify-center gap-3 px-10 py-4 bg-[#D4AF37] text-[#0A0607] font-body text-sm tracking-[0.2em] uppercase disabled:opacity-60">
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? "Updating..." : "Update Password"}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
